/**
 * parseIpRangeInput — flexibler IP-Eingabe-Parser mit Normalisierung (Issue #318).
 *
 * Ein Platform-Admin tippt das ein, was der MNO geliefert hat — entweder eine
 * CIDR-Notation oder eine Start-Ende-Range. Diese Funktion akzeptiert beide
 * Schreibweisen, validiert IPv4 und IPv6 und normalisiert das Ergebnis in das
 * Ziel-Format der Admin-API (Arbeitsannahme: CIDR).
 *
 * Die IPv6-/BigInt-Adressmathematik (`::`-Expansion, Hex-Gruppen, Kompression)
 * kommt aus der geprueften Library `ip-bigint` — kein hand-gerollter v6-Parser.
 * Die Range→CIDR-Aufteilung selbst ist ein versions-agnostischer Greedy-Loop
 * ueber Ganzzahlen (identisch fuer v4 und v6, getrieben von der Bitbreite).
 *
 * Bewusste Entscheidung: vertauschte Grenzen (Ende < Start) werden als Fehler
 * gemeldet, NICHT still getauscht — bei einem Admin-Werkzeug ist ein Tippfehler
 * wahrscheinlicher als eine absichtliche Rueckwaerts-Range.
 */
import { parseIp, stringifyIp } from 'ip-bigint';

export type IpVersion = 4 | 6;

export interface ParsedIpRange {
  /** Normalisiertes Ziel-Format (CIDR), minimaler Satz fuer eine Range. */
  cidrs: string[];
  version: IpVersion;
}

export interface ParseError {
  /** Stabiler Fehlercode — die UI mappt ihn auf eine i18n-Meldung. */
  error: ParseErrorCode;
}

export type ParseErrorCode =
  | 'EMPTY'
  | 'INVALID_IP'
  | 'INVALID_CIDR_PREFIX'
  | 'MIXED_VERSIONS'
  | 'REVERSED_RANGE'
  | 'INVALID_FORMAT';

export type ParseIpRangeResult = ParsedIpRange | ParseError;

export function isParseError(result: ParseIpRangeResult): result is ParseError {
  return (result as ParseError).error !== undefined;
}

/**
 * Ziel-Format der Admin-API: CIDR — bestätigt durch den Facade-Contract v2.10
 * (Spec 0489/#326). Der Request schickt pro Range nur `Cidr`; die Konstante +
 * Funktion bleiben als isolierte Normalisierungs-Naht erhalten.
 */
export const TARGET_FORMAT = 'cidr' as const;

export function normalizeToTargetFormat(cidrs: string[]): string[] {
  return cidrs;
}

// ── Low-level Helpers (BigInt) ────────────────────────────────────────────

interface SingleIp {
  number: bigint;
  version: IpVersion;
}

/** Strikte Validierung + Parsing einer einzelnen IP (v4 oder v6) → BigInt. */
function parseSingleIp(raw: string): SingleIp | null {
  const s = raw.trim();
  if (s === '') return null;

  if (s.includes(':')) {
    // IPv6 — Library uebernimmt Validierung (lehnt z. B. `gggg` ab).
    try {
      const { number, version } = parseIp(s);
      if (version !== 6) return null;
      return { number, version: 6 };
    } catch {
      return null;
    }
  }

  // IPv4 — eigene strikte Oktett-Pruefung. ip-bigint akzeptiert hier faelschlich
  // Oktette > 255 (z. B. 192.0.2.256), daher bewusst nicht delegiert.
  const m = /^(\d{1,3})\.(\d{1,3})\.(\d{1,3})\.(\d{1,3})$/.exec(s);
  if (!m) return null;
  let number = 0n;
  for (let i = 1; i <= 4; i++) {
    const octet = Number(m[i]);
    if (octet > 255) return null;
    number = (number << 8n) | BigInt(octet);
  }
  return { number, version: 4 };
}

function totalBitsFor(version: IpVersion): bigint {
  return version === 4 ? 32n : 128n;
}

/** BigInt → IP-String. v6 ueber ip-bigint (komprimiert), v4 selbst. */
function formatIp(number: bigint, version: IpVersion): string {
  if (version === 6) {
    return stringifyIp({ number, version: 6 });
  }
  return [24n, 16n, 8n, 0n].map((shift) => ((number >> shift) & 255n).toString()).join('.');
}

function trailingZeros(n: bigint): bigint {
  let count = 0n;
  let v = n;
  while ((v & 1n) === 0n) {
    v >>= 1n;
    count++;
  }
  return count;
}

function floorLog2(n: bigint): bigint {
  let count = 0n;
  let v = n;
  while (v > 1n) {
    v >>= 1n;
    count++;
  }
  return count;
}

/**
 * Versions-agnostischer Greedy-Algorithmus: zerlegt eine zusammenhaengende
 * Range [start, end] in den minimalen Satz ausgerichteter CIDR-Bloecke.
 */
function rangeToCidrs(start: bigint, end: bigint, version: IpVersion): string[] {
  const totalBits = totalBitsFor(version);
  const cidrs: string[] = [];
  let current = start;

  while (current <= end) {
    // Groesster an `current` ausgerichteter Block (durch Trailing-Zeros begrenzt).
    const hostBitsByAlignment = current === 0n ? totalBits : trailingZeros(current);
    // Groesster Block, der in den Rest [current, end] passt.
    const remaining = end - current + 1n;
    const hostBitsByRemaining = floorLog2(remaining);
    const hostBits =
      hostBitsByAlignment < hostBitsByRemaining ? hostBitsByAlignment : hostBitsByRemaining;

    const prefix = totalBits - hostBits;
    cidrs.push(`${formatIp(current, version)}/${prefix}`);

    if (hostBits >= totalBits) break; // ganzer Adressraum abgedeckt
    current += 1n << hostBits;
  }

  return cidrs;
}

// ── Form-spezifische Parser ───────────────────────────────────────────────

function parseCidrInput(input: string): ParseIpRangeResult {
  const parts = input.split('/');
  if (parts.length !== 2) return { error: 'INVALID_FORMAT' };

  const ip = parseSingleIp(parts[0]);
  if (!ip) return { error: 'INVALID_IP' };

  const prefixStr = parts[1].trim();
  if (!/^\d+$/.test(prefixStr)) return { error: 'INVALID_CIDR_PREFIX' };

  const totalBits = totalBitsFor(ip.version);
  const prefix = BigInt(prefixStr);
  if (prefix < 0n || prefix > totalBits) return { error: 'INVALID_CIDR_PREFIX' };

  // Auf die Netzadresse normalisieren (Host-Bits maskieren).
  const hostBits = totalBits - prefix;
  const network = (ip.number >> hostBits) << hostBits;
  return {
    cidrs: normalizeToTargetFormat([`${formatIp(network, ip.version)}/${prefix}`]),
    version: ip.version,
  };
}

function parseRangeInput(input: string): ParseIpRangeResult {
  const idx = input.indexOf('-');
  const left = input.slice(0, idx).trim();
  const right = input.slice(idx + 1).trim();
  if (left === '' || right === '') return { error: 'INVALID_FORMAT' };

  const a = parseSingleIp(left);
  const b = parseSingleIp(right);
  if (!a || !b) return { error: 'INVALID_IP' };
  if (a.version !== b.version) return { error: 'MIXED_VERSIONS' };
  if (b.number < a.number) return { error: 'REVERSED_RANGE' };

  return {
    cidrs: normalizeToTargetFormat(rangeToCidrs(a.number, b.number, a.version)),
    version: a.version,
  };
}

// ── Public API ────────────────────────────────────────────────────────────

/**
 * Parst eine CIDR-Notation, eine Start-Ende-Range oder eine einzelne IP und
 * gibt den normalisierten CIDR-Satz + die IP-Version zurueck — oder einen
 * klaren Validierungsfehler.
 */
export function parseIpRangeInput(raw: string): ParseIpRangeResult {
  const input = (raw ?? '').trim();
  if (input === '') return { error: 'EMPTY' };

  // Reihenfolge: CIDR (enthaelt '/') vor Range (enthaelt '-'). Weder v4 noch v6
  // enthalten '-', daher ist das erste '-' eindeutig der Range-Trenner.
  if (input.includes('/')) return parseCidrInput(input);
  if (input.includes('-')) return parseRangeInput(input);

  // Einzelne IP → /32 (v4) bzw. /128 (v6).
  const ip = parseSingleIp(input);
  if (!ip) return { error: 'INVALID_IP' };
  const prefix = ip.version === 4 ? 32 : 128;
  return {
    cidrs: normalizeToTargetFormat([`${formatIp(ip.number, ip.version)}/${prefix}`]),
    version: ip.version,
  };
}

export interface CollectResult {
  /** Aggregierte, deduplizierte CIDRs aller gueltigen Zeilen. */
  cidrs: string[];
  /**
   * Informativ: die gemeinsame Version, falls alle Zeilen dieselbe haben — sonst
   * null. Seit Spec 326 sind gemischte v4/v6 erlaubt; dieses Feld blockt nichts
   * mehr (der Server leitet die Version pro CIDR ab).
   */
  version: IpVersion | null;
  /** Fehlercode je Zeilen-Index (leere Zeilen werden ignoriert). */
  errors: Record<number, ParseErrorCode>;
  /** Informativ: mehrere gueltige Zeilen mit unterschiedlicher IP-Version (erlaubt). */
  mixedVersions: boolean;
  /** Mindestens eine Zeile wurde erfolgreich geparst. */
  hasValidRange: boolean;
  /** Gesamt-Eintrag ist speicherbar. */
  valid: boolean;
}

/**
 * Aggregiert mehrere Roh-Eingaben (Multi-Range-Feld) zu einem Eintrag: sammelt
 * alle CIDRs und meldet Zeilenfehler. Leere Zeilen werden ignoriert. Gemischte
 * v4/v6 sind seit Spec 326 erlaubt — `valid` haengt NICHT mehr von `mixedVersions`
 * ab (die Version wird server-seitig pro CIDR abgeleitet, ist hier nur Anzeige).
 */
export function collectIpRanges(rawList: string[]): CollectResult {
  const cidrSet = new Set<string>();
  const errors: Record<number, ParseErrorCode> = {};
  const versions = new Set<IpVersion>();

  rawList.forEach((raw, index) => {
    if ((raw ?? '').trim() === '') return;
    const result = parseIpRangeInput(raw);
    if (isParseError(result)) {
      errors[index] = result.error;
      return;
    }
    versions.add(result.version);
    result.cidrs.forEach((cidr) => cidrSet.add(cidr));
  });

  const mixedVersions = versions.size > 1;
  const hasValidRange = cidrSet.size > 0;
  const version = versions.size === 1 ? [...versions][0] : null;
  const valid = hasValidRange && Object.keys(errors).length === 0;

  return { cidrs: [...cidrSet], version, errors, mixedVersions, hasValidRange, valid };
}
